import { useEffect } from 'react';
import {
  resetReportPrintSectionFit,
  scheduleReportPrintSectionFit,
} from '../utils/reportPrintFit.util';

/**
 * Measures report sections and scales them to one printed page before print.
 * Fit runs only for print (beforeprint / print media), not on screen preview load,
 * so the print route stays scrollable while tuning sections.
 * @param enabled - When false, listeners are not attached.
 */
export const useReportPrintSectionFit = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const onBeforePrint = () => {
      resetReportPrintSectionFit();
      scheduleReportPrintSectionFit();
    };

    const onAfterPrint = () => {
      resetReportPrintSectionFit();
      window.requestAnimationFrame(() => {
        resetReportPrintSectionFit();
      });
    };

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);

    if (window.matchMedia) {
      const media = window.matchMedia('print');
      const onPrintChange = (event: MediaQueryListEvent) => {
        if (event.matches) {
          scheduleReportPrintSectionFit();
        } else {
          resetReportPrintSectionFit();
        }
      };
      media.addEventListener('change', onPrintChange);

      return () => {
        window.removeEventListener('beforeprint', onBeforePrint);
        window.removeEventListener('afterprint', onAfterPrint);
        media.removeEventListener('change', onPrintChange);
        resetReportPrintSectionFit();
      };
    }

    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      resetReportPrintSectionFit();
    };
  }, [enabled]);
};
